import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  User,
  Mail,
  Bell,
  Inbox,
  Loader2,
  Save,
  CreditCard,
  Calendar,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    notification_email: user?.notification_email ?? true,
    notification_inapp: user?.notification_inapp ?? true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/settings`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success(`Subscription cancelled. Access until ${new Date(response.data.access_until).toLocaleDateString()}`);
      setShowCancelConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-8" data-testid="settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your account and notification preferences</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#E2F5EA] flex items-center justify-center">
                <User className="w-5 h-5 text-[#00A651]" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Profile Information</h2>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="full_name" className="text-slate-700 mb-2 block">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Dr. John Smith"
                  className="h-11"
                  data-testid="fullname-input"
                />
              </div>

              <div>
                <Label className="text-slate-700 mb-2 block">Email Address</Label>
                <div className="flex items-center gap-2 h-11 px-3 bg-slate-50 rounded-md border border-slate-200">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{user?.email}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Notification Preferences</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive job alerts via email</p>
                  </div>
                </div>
                <Switch
                  checked={formData.notification_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, notification_email: checked })}
                  data-testid="email-notifications-toggle"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Inbox className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900">In-App Notifications</p>
                    <p className="text-sm text-slate-500">See notifications in your dashboard</p>
                  </div>
                </div>
                <Switch
                  checked={formData.notification_inapp}
                  onCheckedChange={(checked) => setFormData({ ...formData, notification_inapp: checked })}
                  data-testid="inapp-notifications-toggle"
                />
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Subscription</h2>
            </div>

            <div className={`p-4 rounded-lg ${user?.subscription_active ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-semibold ${user?.subscription_active ? 'text-green-800' : 'text-amber-800'}`}>
                    {user?.subscription_active ? 'Active Subscription' : 'No Active Subscription'}
                  </p>
                  {user?.subscription_active && user?.subscription_expires && (
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <Calendar className="w-4 h-4" />
                      {user?.subscription_cancel_at_period_end 
                        ? `Access ends: ${new Date(user.subscription_expires).toLocaleDateString()}`
                        : `Renews: ${new Date(user.subscription_expires).toLocaleDateString()}`
                      }
                    </p>
                  )}
                  {user?.subscription_cancel_at_period_end && (
                    <p className="text-sm text-amber-600 mt-1">Cancellation scheduled</p>
                  )}
                </div>
                {!user?.subscription_active && (
                  <Button
                    type="button"
                    className="bg-[#00A651] hover:bg-[#008C44] text-white"
                    onClick={() => window.location.href = '/subscription'}
                  >
                    Subscribe
                  </Button>
                )}
              </div>
            </div>

            {/* Cancel Subscription */}
            {user?.subscription_active && user?.has_stripe_subscription && !user?.subscription_cancel_at_period_end && (
              <div className="mt-4">
                {!showCancelConfirm ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setShowCancelConfirm(true)}
                    data-testid="cancel-subscription-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                ) : (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">Are you sure you want to cancel?</p>
                        <p className="text-sm text-red-600 mt-1">
                          You'll continue to have access until {user?.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'the end of your billing period'}.
                        </p>
                        <div className="flex gap-3 mt-4">
                          <Button
                            type="button"
                            variant="destructive"
                            disabled={cancelLoading}
                            onClick={handleCancelSubscription}
                            data-testid="confirm-cancel-btn"
                          >
                            {cancelLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Cancelling...
                              </>
                            ) : (
                              'Yes, Cancel'
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowCancelConfirm(false)}
                          >
                            Keep Subscription
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#00A651] hover:bg-[#008C44] text-white"
            data-testid="save-settings-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
